import { useState, useEffect, useCallback, useMemo } from 'react'
import { useBestaetigung, Reiterleiste, type ReiterEintrag } from '@pilot-hub/ui'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { ListenTabelle, type SpalteDef } from '../components/ListenTabelle'
import { usePersistState, FilterZurueck, KennzahlenLeiste } from '../components/ListenWerkzeuge'
import { FormAktionsleiste, useUngespeichert } from '../components/Formular'
import { ZeilenAktionen } from '../components/ZeilenAktionen'
import { StatusBadge } from '../components/StatusBadge'
import { FehltChip } from '../components/Chips'
import { Statusband } from '../components/Statusband'
import { PageHeader, Card, Button, Input, Select, Textarea } from '@pilot-hub/ui'
import { flightApi, droneApi, userApi, akkuApi, missionApi, api } from '@pilot-hub/api-client'
import { ArrowLeft, Plus, Edit, Eye, Trash2, FileText, Save, ExternalLink } from 'lucide-react'
import { EinsatzortDisplay } from '../components/EinsatzortDisplay'
import { GpsButton } from '../components/GpsButton'
import { FlugroutenKarte } from '../components/FlugroutenKarte'
import { FlugAufnahme } from '../components/FlugAufnahme'
import { akkuAnzeige } from '../utils/akku'
import { FlugartWahl } from '../components/FlugartWahl'
import { VorkommnisFelder, VorkommnisWert, vorkommnisAusApi, vorkommnisZuApi, vorkommnisFehler, vorkommnisText, vorkommnisBemerkungZuApi, useFlugHms } from '../components/VorkommnisFelder'
import { DatumZeitFeld } from '../components/DatumFelder'

interface Flug {
  id: number
  dji_flight_id: string | null
  einsatz_id: number | null
  drohne_id: number | null
  pilot_id: number | null
  co_pilot_id: number | null
  beobachter_id: number | null
  akku_id: number | null
  startzeit: string | null
  endzeit: string | null
  startort: string | null
  zielort: string | null
  wetter: string | null
  flugart: string | null
  vorkommnis: boolean | null
  vorkommnis_bemerkung: string | null
  bemerkungen: string | null
}

const LEER = {
  einsatz_id: '',
  drohne_id: '',
  pilot_id: '',
  co_pilot_id: '',
  beobachter_id: '',
  akku_id: '',
  startzeit: '',
  endzeit: '',
  startort: '',
  zielort: '',
  wetter: '',
  flugart: '',
  vorkommnis: 'NEIN' as VorkommnisWert,
  vorkommnis_bemerkung: '',
  bemerkungen: ''
}

function dauer(a: any): string {
  if (!a.startzeit || !a.endzeit) return '--'
  const ms = new Date(a.endzeit).getTime() - new Date(a.startzeit).getTime()
  if (ms <= 0) return '--'
  const min = Math.round(ms / 60000)
  return min >= 60 ? Math.floor(min / 60) + ' h ' + (min % 60) + ' min' : min + ' min'
}

/** Pflichtzuordnung fehlt: Flug ohne Pilot oder ohne Drohne */
const unvollstaendig = (f: { pilot_id?: number | null; drohne_id?: number | null }) => !f.pilot_id || !f.drohne_id
const TYP_TEXT: Record<string, string> = { EINSATZ: 'Einsatz', UEBUNGSEINSATZ: 'Übungseinsatz', UEBUNG: 'Übung' }
const STATUS_TEXT: Record<string, string> = { GEPLANT: 'geplant', AKTIV: 'aktiv', LAUFEND: 'laufend', ABGESCHLOSSEN: 'abgeschlossen', BEENDET: 'beendet' }

/** Flugart nach Sichtverhältnis (EGRED 2, Flugbuch) */
const FLUGARTEN = [
  { value: 'VLOS', label: 'VLoS – in Sichtweite des Piloten' },
  { value: 'EVLOS', label: 'EVLoS – erweiterte Sichtweite (Luftraumbeobachter)' },
  { value: 'BVLOS', label: 'BVLoS – außerhalb der Sichtweite' },
]
const flugartKurz = (v: string | null | undefined) => ({ VLOS: 'VLoS', EVLOS: 'EVLoS', BVLOS: 'BVLoS' } as Record<string, string>)[v ?? ''] ?? '--'

const fmtDT = (s: string | null) => (s ? new Date(s).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '--')

type FlugReiter = 'uebersicht' | 'route' | 'aufnahmen' | 'vorkommnisse'

type ViewMode = 'list' | 'create' | 'edit' | 'detail'
type ViewState = { mode: ViewMode; flugId?: number }

export function FlightsPage() {
  const { bestaetigen, dialog } = useBestaetigung()
  const [fluege, setFluege] = useState<any[]>([])
  const [einsaetze, setEinsaetze] = useState<Array<{ id: number; label: string; typ?: string | null; status?: string | null }>>([])
  // alle Drohnen (für Anzeige/Filter); auswählbar sind nur verfügbare/einsatzbereite (wie /drohnen/auswahl)
  const [drohnen, setDrohnen] = useState<Array<{ id: number; label: string; waehlbar: boolean }>>([])
  const [nutzer, setNutzer] = useState<Array<{ id: number; label: string }>>([])
  const [coPiloten, setCoPiloten] = useState<Array<{ id: number; label: string }>>([])
  const [luftraumbeobachter, setLuftraumbeobachter] = useState<Array<{ id: number; label: string }>>([])
  const [akkus, setAkkus] = useState<Array<{ id: number; label: string; drohneIds: number[] }>>([])

  const [view, setView] = useState<{ mode: ViewMode; flugId?: number }>({ mode: 'list' })
  const [form, setForm] = useState<Record<string, string>>({ 
    einsatz_id: '', drohne_id: '', pilot_id: '', co_pilot_id: '', beobachter_id: '', 
    akku_id: '', startzeit: '', endzeit: '', startort: '', zielort: '', 
    wetter: '', flugart: '', bemerkungen: '' 
  })
  const [speichert, setSpeichert] = useState(false)
  const [loading, setLoading] = useState(true)
  const [fehler, setFehler] = useState<string | null>(null)
  const [reiter, setReiter] = useState<FlugReiter>('uebersicht')
  const [fehlerReiter, setFehlerReiter] = useState<FlugReiter | null>(null)
  const waehleReiter = (k: FlugReiter) => { setReiter(k); setFehlerReiter(f => (f === k ? null : f)) }
  const [berichtLaeuft, setBerichtLaeuft] = useState(false)
  const [berichtUmfang, setBerichtUmfang] = useState<'ausfuehrlich' | 'kurz'>('ausfuehrlich')

  // Filter states
  const [jahrFilter, setJahrFilter] = usePersistState<string>('fluege.jahrFilter', 'all')
  const [pilotFilter, setPilotFilter] = usePersistState<string>('fluege.pilotFilter', 'all')
  const [unvollFilter, setUnvollFilter] = usePersistState<boolean>('fluege.unvollstaendig', false)
  const [zuordnenId, setZuordnenId] = useState('')
  const [zuordnenLaeuft, setZuordnenLaeuft] = useState(false)
  const [drohneFilter, setDrohneFilter] = usePersistState<string>('fluege.drohneFilter', 'all')
  const [einsatzTypFilter, setEinsatzTypFilter] = usePersistState<string>('fluege.einsatzTypFilter', 'all')
  const [search, setSearch] = usePersistState('fluege.search', '')

  // Drohnen, die dem gewählten Einsatz zugeordnet sind (aktiv gebunden): in der Auswahl oben, gekennzeichnet
  const [einsatzDrohnenIds, setEinsatzDrohnenIds] = useState<number[]>([])
  useEffect(() => {
    const eid = Number(form.einsatz_id)
    if (!eid) { setEinsatzDrohnenIds([]); return }
    let abgebrochen = false
    api.get(`/einsaetze/${eid}/drohnen`)
      .then(r => { if (!abgebrochen) setEinsatzDrohnenIds(((r.data as any[]) ?? []).filter(x => !x.freigegeben).map(x => x.drohne_id)) })
      .catch(() => { if (!abgebrochen) setEinsatzDrohnenIds([]) })
    return () => { abgebrochen = true }
  }, [form.einsatz_id])

  const geaendert = useUngespeichert(form, JSON.stringify(view))
  const laden = useCallback(async () => {
    setLoading(true); setFehler(null)
    try {
      const mapU = (x: any) => ({ id: x.id, label: ((x.vorname ?? '') + ' ' + (x.nachname ?? '')).trim() || x.benutzername })
      const [f, e, d, p, cp, lb, a] = await Promise.all([
        flightApi.list({ size: 500 }),
        // alle Einsätze (auch abgeschlossene), damit Flüge zu beendeten Einsätzen richtig angezeigt werden
        missionApi.list({ size: 1000 }),
        droneApi.list({ size: 500 }),
        // Dropdowns nach FUNKTION filtern (wie im Piloten-Modul)
        userApi.list({ size: 500, funktion: 'PILOT' }),
        userApi.list({ size: 500, funktion: 'CO_PILOT' }),
        userApi.list({ size: 500, funktion: 'LUFTRAUM_BEOBACHTER' }),
        akkuApi.list({ size: 500 })
      ])
      setFluege((f.data as any).items ?? (f.data as any) ?? [])
      setEinsaetze([{ id: 0, label: '-- Kein Einsatz --' }, ...(((e.data as any)?.items ?? (e.data as any) ?? []) as any[]).map((e: any) => ({ id: e.id, label: e.titel + ' (' + (STATUS_TEXT[e.status] ?? e.status) + ')', typ: e.typ, status: e.status }))])
      setDrohnen(((d.data as any).items ?? (d.data as any) ?? []).map((x: any) => ({
        id: x.id,
        label: [x.asb_inventarnummer, [x.hersteller, x.modell].filter(Boolean).join(' ')].filter(Boolean).join(' – ') || x.kennzeichen || 'Drohne #' + x.id,
        waehlbar: ['VERFUEGBAR', 'EINSATZBEREIT'].includes(x.status),
      })))
      setNutzer(((p.data as any).items ?? (p.data as any) ?? []).map(mapU))
      setCoPiloten(((cp.data as any).items ?? (cp.data as any) ?? []).map(mapU))
      setLuftraumbeobachter(((lb.data as any).items ?? (lb.data as any) ?? []).map(mapU))
      setAkkus(((a.data as any)?.items ?? []).map((x: any) => ({ id: x.id, label: akkuAnzeige(x), drohneIds: (x.drohne_ids ?? []) as number[] })))
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Flüge konnten nicht geladen werden')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { laden() }, [laden])

  // Aus der Einsatzansicht: /flights?oeffnen=<id> öffnet die Flugansicht dieses Flugs
  const [suchParams, setSuchParams] = useSearchParams()
  const navigate = useNavigate()
  useEffect(() => {
    const id = Number(suchParams.get('oeffnen'))
    if (!id || loading) return
    const f = fluege.find(x => x.id === id)
    if (f) oeffneDetail(f)
    else setFehler('Der Flug wurde nicht gefunden.')
    setSuchParams({}, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, fluege, suchParams])

  const getLabel = (liste: Array<{ id: number; label: string }>, id: number | null) =>
    id ? (liste.find(x => x.id === id)?.label || '#' + id) : '--'

  /** Einsatztyp des Flugs als Anzeigetext (Filter „Einsatztyp“) */
  const getEinsatzTyp = (einsatzId: number | null) => {
    if (!einsatzId || einsatzId === 0) return 'Ohne Einsatz'
    const typ = einsaetze.find(e => e.id === einsatzId)?.typ
    return typ ? (TYP_TEXT[typ] ?? typ) : 'Unbekannt'
  }

  function oeffneNeu() {
    setReiter('uebersicht'); setFehlerReiter(null); setFehler(null)
    setForm({ 
      einsatz_id: '', drohne_id: '', pilot_id: '', co_pilot_id: '', beobachter_id: '', 
      akku_id: '', startzeit: '', endzeit: '', startort: '', zielort: '', 
      wetter: '', flugart: '', vorkommnis: 'NEIN', vorkommnis_bemerkung: '', bemerkungen: '' 
    })
    setView({ mode: 'create' })
  }

  function oeffneBearbeiten(f: any) {
    // Ansicht -> Bearbeiten desselben Flugs behält den Reiter, sonst „Übersicht“
    setReiter(view.mode === 'detail' && view.flugId === f.id ? reiter : 'uebersicht'); setFehlerReiter(null)
    setForm({
      einsatz_id: f.einsatz_id ? String(f.einsatz_id) : '',
      drohne_id: f.drohne_id ? String(f.drohne_id) : '',
      pilot_id: f.pilot_id ? String(f.pilot_id) : '',
      co_pilot_id: f.co_pilot_id ? String(f.co_pilot_id) : '',
      beobachter_id: f.beobachter_id ? String(f.beobachter_id) : '',
      akku_id: f.akku_id ? String(f.akku_id) : '',
      startzeit: f.startzeit ? f.startzeit.slice(0, 16) : '',
      endzeit: f.endzeit ? f.endzeit.slice(0, 16) : '',
      startort: f.startort ?? '',
      zielort: f.zielort ?? '',
      wetter: f.wetter ?? '',
      flugart: f.flugart ?? '',
      vorkommnis: vorkommnisAusApi(f.vorkommnis),
      vorkommnis_bemerkung: f.vorkommnis_bemerkung ?? '',
      bemerkungen: f.bemerkungen ?? ''
    })
    setView({ mode: 'edit', flugId: f.id })
  }

  function oeffneDetail(f: any) {
    setReiter('uebersicht'); setFehlerReiter(null)
    setView({ mode: 'detail', flugId: f.id })
    setForm({
      einsatz_id: String(f.einsatz_id ?? ''),
      drohne_id: String(f.drohne_id ?? ''),
      pilot_id: String(f.pilot_id ?? ''),
      co_pilot_id: String(f.co_pilot_id ?? ''),
      beobachter_id: String(f.beobachter_id ?? ''),
      akku_id: String(f.akku_id ?? ''),
      startzeit: f.startzeit ? f.startzeit.slice(0, 16) : '',
      endzeit: f.endzeit ? f.endzeit.slice(0, 16) : '',
      startort: f.startort || '',
      zielort: f.zielort || '',
      wetter: f.wetter || '',
      flugart: f.flugart || '',
      vorkommnis: vorkommnisAusApi(f.vorkommnis),
      vorkommnis_bemerkung: f.vorkommnis_bemerkung || '',
      bemerkungen: f.bemerkungen || '',
    })
  }

  function zurueckZurListe() {
    setView({ mode: 'list' })
    setForm({ 
      einsatz_id: '', drohne_id: '', pilot_id: '', co_pilot_id: '', beobachter_id: '', 
      akku_id: '', startzeit: '', endzeit: '', startort: '', zielort: '', 
      wetter: '', flugart: '', vorkommnis: 'NEIN', vorkommnis_bemerkung: '', bemerkungen: '' 
    })
  }

  async function speichern() {
    const vkFehler = vorkommnisFehler(form.vorkommnis as VorkommnisWert, form.vorkommnis_bemerkung)
    if (vkFehler) { setFehler(vkFehler); setFehlerReiter('vorkommnisse'); setReiter('vorkommnisse'); return }
    setFehler(null); setFehlerReiter(null)
    setSpeichert(true)
    try {
      const payload = {
        einsatz_id: form.einsatz_id ? Number(form.einsatz_id) : null,
        drohne_id: form.drohne_id ? Number(form.drohne_id) : null,
        pilot_id: form.pilot_id ? Number(form.pilot_id) : null,
        co_pilot_id: form.co_pilot_id ? Number(form.co_pilot_id) : null,
        beobachter_id: form.beobachter_id ? Number(form.beobachter_id) : null,
        akku_id: form.akku_id ? Number(form.akku_id) : null,
        startzeit: form.startzeit || null,
        endzeit: form.endzeit || null,
        startort: form.startort || null,
        zielort: form.zielort || null,
        wetter: form.wetter || null,
        flugart: form.flugart || null,
        vorkommnis: vorkommnisZuApi(form.vorkommnis as VorkommnisWert),
        vorkommnis_bemerkung: vorkommnisBemerkungZuApi(form.vorkommnis as VorkommnisWert, form.vorkommnis_bemerkung),
        bemerkungen: form.bemerkungen || null
      }
      if (view.mode === 'edit' && view.flugId) await flightApi.update(view.flugId, payload)
      else await flightApi.create(payload)
      setView({ mode: 'list' })
      await laden()
    } catch (e: any) {
      const text = typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Speichern fehlgeschlagen'
      setFehler(text)
      const ziel: FlugReiter = /vorkommnis/i.test(text) ? 'vorkommnisse' : 'uebersicht'
      setFehlerReiter(ziel); setReiter(ziel)
    } finally {
      setSpeichert(false)
    }
  }

  /** Fehlende Drohne nachtragen: bestehender Endpunkt PUT /fluege/{id} mit nur diesem Feld */
  async function droheZuordnen(id: number) {
    setZuordnenLaeuft(true); setFehler(null)
    try {
      await flightApi.update(id, { drohne_id: Number(zuordnenId) })
      setForm(f => ({ ...f, drohne_id: zuordnenId })); setZuordnenId('')
      await laden()
    } catch (e: any) {
      setFehler(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Drohne konnte nicht zugeordnet werden')
    } finally { setZuordnenLaeuft(false) }
  }

  /** Flugbericht (PDF mit Karte der Flugroute) erzeugen und herunterladen; kurz = Übersicht mit Karte */
  async function flugberichtLaden(id: number) {
    setBerichtLaeuft(true); setFehler(null)
    try {
      const { api } = await import('@pilot-hub/api-client')
      const res = await api.get(`/fluege/${id}/flugbericht`, { params: { umfang: berichtUmfang }, responseType: 'blob' })
      const name = res.headers?.['content-disposition']?.match(/filename="?([^";]+)"?/)?.[1] || `Flugbericht_${id}.pdf`
      const url = URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = name
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (e: any) {
      const data = e?.response?.data
      let text = 'Flugbericht konnte nicht erstellt werden'
      if (data instanceof Blob) { try { text = JSON.parse(await data.text())?.detail || text } catch { /* Text bleibt */ } }
      setFehler(text)
    } finally {
      setBerichtLaeuft(false)
    }
  }

  async function loeschen(id: number) {
    try {
      await (await import('@pilot-hub/api-client')).api.delete('/fluege/' + id)
      await laden()
    } catch (e: any) {
      setFehler(e?.response?.data?.detail || 'Löschen fehlgeschlagen')
    }
  }

  const gefiltertBasis = useMemo(() => {
    return fluege.filter(f => {
      if (jahrFilter !== 'all' && f.startzeit) {
        const jahr = new Date(f.startzeit).getFullYear().toString()
        if (jahr !== jahrFilter) return false
      }
      if (pilotFilter !== 'all' && String(f.pilot_id) !== pilotFilter) return false
      if (drohneFilter !== 'all' && String(f.drohne_id) !== drohneFilter) return false
      if (einsatzTypFilter !== 'all') {
        const einsatzTyp = getEinsatzTyp(f.einsatz_id)
        if (einsatzTyp !== einsatzTypFilter) return false
      }
      if (search && !(
        (f.startort ?? '').toLowerCase().includes(search.toLowerCase()) ||
        (f.zielort ?? '').toLowerCase().includes(search.toLowerCase()) ||
        getLabel(nutzer, f.pilot_id).toLowerCase().includes(search.toLowerCase()) ||
        (f.flugart ?? '').toLowerCase().includes(search.toLowerCase()) ||
        flugartKurz(f.flugart).toLowerCase().includes(search.toLowerCase())
      )) return false
      return true
    })
  }, [fluege, jahrFilter, pilotFilter, drohneFilter, einsatzTypFilter, search, nutzer, einsaetze])
  const unvollstaendigAnzahl = useMemo(() => gefiltertBasis.filter(unvollstaendig).length, [gefiltertBasis])
  const gefiltert = useMemo(() => unvollFilter ? gefiltertBasis.filter(unvollstaendig) : gefiltertBasis, [gefiltertBasis, unvollFilter])

  const verfuegbareJahre = useMemo(() => {
    const jahre = new Set(fluege.map(f => f.startzeit ? new Date(f.startzeit).getFullYear() : null).filter((v): v is number => v !== null))
    return Array.from(jahre).sort((a, b) => b - a).map(String)
  }, [fluege])

  const verfuegbareEinsatzTypen = useMemo(() => {
    const typen = new Set(fluege.map(f => getEinsatzTyp(f.einsatz_id)).filter(Boolean))
    return Array.from(typen).sort()
  }, [fluege, einsaetze])

  // Stats based on filtered data
  const stats = useMemo(() => ({
    gesamt: gefiltert.length,
    stunden: (gefiltert.reduce((sum, f) => {
      if (!f.startzeit || !f.endzeit) return sum
      const ms = new Date(f.endzeit).getTime() - new Date(f.startzeit).getTime()
      return ms > 0 ? sum + ms / 60000 : sum
    }, 0) / 60).toFixed(1),
    abgeschlossen: gefiltert.filter(f => !!f.endzeit).length
  }), [gefiltert])

  // Columns for DataTable
  const columns: SpalteDef[] = [
    { key: 'startzeit', header: 'Start', accessor: 'startzeit', sortWert: (f: any) => f.startzeit ? new Date(f.startzeit).getTime() : null, render: (_v: any, f: any) => fmtDT(f.startzeit) },
    { key: 'pilot', header: 'Pilot', accessor: 'pilot_id', sortWert: (f: any) => getLabel(nutzer, f.pilot_id), render: (_v: any, f: any) => f.pilot_id ? getLabel(nutzer, f.pilot_id) : <FehltChip /> },
    { key: 'drohne', header: 'Drohne', accessor: 'drohne_id', sortWert: (f: any) => getLabel(drohnen, f.drohne_id), render: (_v: any, f: any) => f.drohne_id ? getLabel(drohnen, f.drohne_id) : <FehltChip /> },
    { key: 'route', header: 'Strecke', accessor: 'startort', mobile: 'meta', render: (_v: any, f: any) => (
      <span className="inline-flex items-center gap-1.5">
        <EinsatzortDisplay value={f.startort} />
        <span className="text-dark-text-muted">→</span>
        <EinsatzortDisplay value={f.zielort} />
      </span>
    ) },
    { key: 'dauer', header: 'Dauer', accessor: 'id', sortWert: (f: any) => f.endzeit ? new Date(f.endzeit).getTime() - new Date(f.startzeit).getTime() : null, render: (_v: any, f: any) => dauer(f) },
    {
      key: 'status', header: 'Status', accessor: 'endzeit', sortWert: (f: any) => f.endzeit ? 1 : 0, render: (_v: any, f: any) =>
        <span className="inline-flex flex-wrap items-center gap-1.5"><StatusBadge wert={f.endzeit ? 'ABGESCHLOSSEN' : 'LAUFEND'} />{unvollstaendig(f) && <FehltChip title="Pilot oder Drohne ist nicht zugeordnet">Zuordnung fehlt</FehltChip>}</span>
    },
    {
      key: 'actions', header: 'Aktionen', accessor: 'id', render: (_v: any, f: any) => (
        <ZeilenAktionen name={String(`${fmtDT(f.startzeit)}, ${getLabel(nutzer, f.pilot_id)}`)} onBearbeiten={() => oeffneBearbeiten(f)} eintraege={f.einsatz_id ? [{ label: 'Zum Einsatz', icon: <ExternalLink className="h-4 w-4" />, onClick: () => navigate(`/missions?oeffnen=${f.einsatz_id}`) }] : undefined} onLoeschen={async () => { const name = `${fmtDT(f.startzeit)}, ${getLabel(nutzer, f.pilot_id)}`; if (await bestaetigen({ titel: 'Flug löschen?', text: 'Flug vom', objekt: String(name), hinweis: 'Die Telemetrie des Fluges wird mit gelöscht, Aufnahmen bleiben erhalten. Das kann nicht rückgängig gemacht werden.' })) loeschen(f.id) }} />
      )
    },
  ]

  // ====== LIST VIEW ======
  // Hook vor dem frühen Return (sonst React-Fehler #310 beim Wechsel Liste -> Bearbeiten)
  const flugHms = useFlugHms(view.mode === 'edit' || view.mode === 'detail' ? view.flugId : null)

  if (view.mode === 'list') {
    return (
      <div className="space-y-6">
        <PageHeader
          title=""
          subtitle="Flugbuch der ASB Drohnenstaffel -- alle erfassten Flüge"
          action={<Button onClick={oeffneNeu}><Plus className="h-4 w-4 mr-2" />Neuer Flug</Button>}
        />

        {fehler && <div className="rounded-lg border border-primary-600/40 bg-primary-600/10 px-4 py-3 text-sm text-red-200">{fehler}</div>}

        {/* Stats Cards - based on filtered data */}
        <KennzahlenLeiste items={[
          { label: 'Flüge', anzahl: stats.gesamt, aktiv: false, onClick: () => undefined },
          { label: 'Flugstunden', anzahl: `${stats.stunden} h`, aktiv: false, onClick: () => undefined },
          { label: 'Abgeschlossen', anzahl: stats.abgeschlossen, aktiv: false, onClick: () => undefined },
          { label: 'Unvollständig', anzahl: unvollstaendigAnzahl, aktiv: unvollFilter, punkt: 'bg-amber-400', onClick: () => setUnvollFilter(!unvollFilter) },
        ]} />

        {/* Filters */}
        <Card className="mb-4">
          <div className="flex flex-col gap-4">
            <Input placeholder="Ort, Flugart, Pilot" value={search} onChange={e => setSearch(e.target.value)} className="w-full lg:w-96" />
            <div className="grid w-full gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Select value={jahrFilter} onChange={e => setJahrFilter(e.target.value)} placeholder="Jahr"
                  options={[{ value: 'all', label: 'Alle Jahre' }, ...verfuegbareJahre.map(j => ({ value: j, label: String(j) }))]} />
              <Select value={pilotFilter} onChange={e => setPilotFilter(e.target.value)} placeholder="Pilot"
                  options={[{ value: 'all', label: 'Alle Piloten' }, ...nutzer.map(u => ({ value: String(u.id), label: u.label }))]} />
              <Select value={drohneFilter} onChange={e => setDrohneFilter(e.target.value)} placeholder="Drohne"
                  options={[{ value: 'all', label: 'Alle Drohnen' }, ...drohnen.map(d => ({ value: String(d.id), label: d.label }))]} />
              <Select value={einsatzTypFilter} onChange={e => setEinsatzTypFilter(e.target.value)} placeholder="Einsatztyp"
                  options={[{ value: 'all', label: 'Alle Einsatztypen' }, ...verfuegbareEinsatzTypen.map(t => ({ value: t, label: t }))]} />
            </div>
            <div className="flex flex-wrap items-center gap-3 text-sm text-dark-text-muted">
              <span className="whitespace-nowrap">{gefiltert.length} von {fluege.length}</span>
              <FilterZurueck aktiv={unvollFilter || !!search || jahrFilter !== 'all' || pilotFilter !== 'all' || drohneFilter !== 'all' || einsatzTypFilter !== 'all'} onClick={() => { setSearch(''); setJahrFilter('all'); setPilotFilter('all'); setDrohneFilter('all'); setEinsatzTypFilter('all'); setUnvollFilter(false) }} />
            </div>
          </div>
        </Card>

        <Card>
          {loading ? <div className="py-8 text-center text-dark-text-muted">Lade Flüge …</div> : (
            <ListenTabelle persistKey="fluege" data={gefiltert} columns={columns} keyAccessor={(f: any) => String(f.id)} onRowClick={oeffneDetail} emptyMessage="Keine Flüge vorhanden" />
          )}
        </Card>
        {dialog}
      </div>
    )
  }

  // ====== DETAIL / CREATE / EDIT VIEW (Reiter) ======
  const currentFlug = view.flugId ? fluege.find(f => f.id === view.flugId) : null
  const isCreate = view.mode === 'create'
  const isEdit = view.mode === 'edit'
  const isDetail = view.mode === 'detail'

  const formDauer = dauer({ startzeit: form.startzeit, endzeit: form.endzeit })
  const hmsRelevant = (flugHms?.meldungen ?? []).filter(m => (m.stufe ?? 0) >= 1).length
  // Zähler: Vorkommnis „Ja“ (1) plus die HMS-Meldungen der Drohne ab Stufe „Achtung“ im Flug
  const vorkommnisAnzahl = (form.vorkommnis === 'JA' ? 1 : 0) + hmsRelevant
  const reiterListe: Array<ReiterEintrag<FlugReiter>> = ([
    { key: 'uebersicht', label: 'Übersicht' },
    ...(!isCreate ? [{ key: 'route', label: 'Flugroute' }, { key: 'aufnahmen', label: 'Aufnahmen' }] : []),
    { key: 'vorkommnisse', label: `Vorkommnisse (${vorkommnisAnzahl})` },
  ] as Array<ReiterEintrag<FlugReiter>>).map(r => ({ ...r, fehler: fehlerReiter === r.key }))
  const ortHinweis = (v: string) => (isDetail && v ? <p className="mt-1 text-xs text-dark-text-subtle"><EinsatzortDisplay value={v} /></p> : null)

  return (
    <div className="space-y-4">
      {dialog}
      {/* Kopfzeile: immer sichtbar */}
      <div className="z-20 space-y-3 bg-dark-bg pb-2 md:sticky md:top-0" data-testid="flug-kopf">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={zurueckZurListe}><ArrowLeft className="h-4 w-4 mr-1" />Zurück</Button>
            <span className="text-sm text-dark-text-muted">{isCreate ? 'Neuer Flug' : isEdit ? 'Flug bearbeiten' : 'Flug anzeigen'}</span>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {isDetail && currentFlug && (
              <>
                <select value={berichtUmfang} onChange={e => setBerichtUmfang(e.target.value as 'ausfuehrlich' | 'kurz')}
                  aria-label="Umfang des Flugberichts"
                  title="Ausführlich: mit Höhenprofil, Akkus, HMS-Meldungen und Flugweg-Protokoll. Kurz: Übersicht mit Karte und Eckdaten."
                  className="h-10 rounded-lg border border-dark-border bg-dark-bg-tertiary px-2 text-sm text-dark-text [@media(pointer:coarse)]:h-11">
                  <option value="ausfuehrlich">Flugbericht ausführlich</option>
                  <option value="kurz">Flugbericht kurz</option>
                </select>
                <Button onClick={() => flugberichtLaden(currentFlug.id)} disabled={berichtLaeuft} title="Flugbericht mit Karte der Flugroute als PDF erstellen">
                  <FileText className="h-4 w-4 mr-1" />{berichtLaeuft ? 'Erstelle PDF…' : 'PDF erstellen'}
                </Button>
              </>
            )}
            {isDetail && currentFlug && <Button onClick={() => oeffneBearbeiten(currentFlug)}><Edit className="h-4 w-4 mr-1" />Bearbeiten</Button>}
          </div>
        </div>
        <Card className="!py-3">
          <h1 className="break-words text-xl font-bold text-dark-text" data-testid="kopf-flugnr">
            {isCreate ? 'Neuer Flug' : `Flug ${currentFlug?.flug_nr || (currentFlug ? '#' + currentFlug.id : '')}`}
          </h1>
          <Statusband
            chips={isCreate ? undefined : <>
              <StatusBadge wert={form.endzeit ? 'ABGESCHLOSSEN' : 'LAUFEND'} />
              {(!form.pilot_id || !form.drohne_id) && <FehltChip title="Pilot oder Drohne ist nicht zugeordnet">Zuordnung fehlt</FehltChip>}
            </>}
            zaehler={[
              { label: 'Dauer', wert: formDauer },
              { label: 'Drohne', wert: form.drohne_id ? getLabel(drohnen, Number(form.drohne_id)) : <FehltChip /> },
              { label: 'Pilot', wert: form.pilot_id ? getLabel(nutzer, Number(form.pilot_id)) : <FehltChip /> },
            ]}
            angaben={<>
              <span>Start: <span className="text-dark-text">{fmtDT(form.startzeit || null)}</span></span>
              <span>Ende: <span className="text-dark-text">{form.endzeit ? fmtDT(form.endzeit) : (isCreate ? '–' : 'läuft noch')}</span></span>
              <span className="min-w-0 break-words">Einsatz: <span className="text-dark-text">{form.einsatz_id && form.einsatz_id !== '0' ? getLabel(einsaetze, Number(form.einsatz_id)) : 'kein Einsatz'}</span></span>
            </>}
          />
          {isDetail && currentFlug && !currentFlug.drohne_id && (
            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2" data-testid="flug-drohne-zuordnen">
              <FehltChip>Zuordnung fehlt</FehltChip>
              <span className="text-sm text-dark-text-muted">Diesem Flug ist keine Drohne zugeordnet.</span>
              <Select value={zuordnenId} onChange={e => setZuordnenId(e.target.value)} placeholder="Drohne wählen" className="w-full sm:w-72"
                options={drohnen.filter(d => d.waehlbar).map(d => ({ value: String(d.id), label: d.label }))} />
              <Button disabled={!zuordnenId || zuordnenLaeuft} onClick={() => droheZuordnen(currentFlug.id)}>Drohne zuordnen</Button>
            </div>
          )}
        </Card>
        {fehler && <div className="rounded-lg border border-primary-600/40 bg-primary-600/10 px-4 py-3 text-sm text-red-200" data-testid="flug-fehler">{fehler}</div>}
        <Reiterleiste reiter={reiterListe} aktiv={reiter} onWechsel={waehleReiter} testid="flug-reiter" />
      </div>

      {reiter === 'uebersicht' && (
      <Card>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Einsatz</label>
              <Select value={form.einsatz_id} onChange={e => setForm({...form, einsatz_id: e.target.value})} disabled={isDetail}
                options={einsaetze.map(e => ({ value: String(e.id), label: e.label }))} placeholder="-- Einsatz wählen --" />
            </div>
            <div>
              <label className="label">Drohne</label>
              <Select value={form.drohne_id} onChange={e => {
                  const did = Number(e.target.value)
                  const akkuPasst = !form.akku_id || !did || (akkus.find(a => String(a.id) === form.akku_id)?.drohneIds ?? []).includes(did)
                  setForm({...form, drohne_id: e.target.value, akku_id: akkuPasst ? form.akku_id : ''})
                }} disabled={isDetail}
                options={(() => {
                  const z = new Set(einsatzDrohnenIds)
                  const liste = drohnen.filter(d => d.waehlbar || String(d.id) === form.drohne_id)
                  const opt = (d: { id: number; label: string }) => ({ value: String(d.id), label: z.has(d.id) ? `★ ${d.label}` : d.label })
                  return [...liste.filter(d => z.has(d.id)).map(opt), ...liste.filter(d => !z.has(d.id)).map(opt)]
                })()} placeholder="-- Drohne wählen --" />
              {einsatzDrohnenIds.length > 0 && <p className="mt-1 text-xs text-dark-text-subtle">★ = diesem Einsatz zugeordnet</p>}
            </div>
          </div>
          <div>
            <label className="label">Akku</label>
            <Select value={form.akku_id} onChange={e => setForm({...form, akku_id: e.target.value})} disabled={isDetail}
              options={[{ value: '', label: '-- Kein Akku --' }, ...akkus
                .filter(a => !form.drohne_id || a.drohneIds.includes(Number(form.drohne_id)) || String(a.id) === form.akku_id)
                .map(a => ({ value: String(a.id), label: a.label }))]} placeholder="-- Optional --" />
            {form.drohne_id && <p className="mt-1 text-xs text-dark-text-subtle">Es werden nur Akkus dieser Drohne angezeigt.</p>}
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="label">Pilot</label>
              <Select value={form.pilot_id} onChange={e => setForm({...form, pilot_id: e.target.value})} disabled={isDetail}
                options={nutzer.map(u => ({ value: String(u.id), label: u.label }))} placeholder="-- Pilot wählen --" />
            </div>
            <div>
              <label className="label">Co-Pilot</label>
              <Select value={form.co_pilot_id} onChange={e => setForm({...form, co_pilot_id: e.target.value})} disabled={isDetail}
                options={[{ value: '', label: '-- Kein Co-Pilot --' }, ...coPiloten.map(u => ({ value: String(u.id), label: u.label }))]} placeholder="-- Optional --" />
            </div>
            <div>
              <label className="label">Luftraumbeobachter</label>
              <Select value={form.beobachter_id} onChange={e => setForm({...form, beobachter_id: e.target.value})} disabled={isDetail}
                options={[{ value: '', label: '-- Kein Luftraumbeobachter --' }, ...luftraumbeobachter.map(u => ({ value: String(u.id), label: u.label }))]} placeholder="-- Optional --" />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="min-w-0">
              <label className="label">Startzeit</label>
              <DatumZeitFeld  value={form.startzeit} onChange={v => setForm({...form, startzeit: v})} disabled={isDetail} className="w-full" />
            </div>
            <div className="min-w-0">
              <label className="label">Endzeit</label>
              <DatumZeitFeld  value={form.endzeit} onChange={v => setForm({...form, endzeit: v})} disabled={isDetail} className="w-full" />
              {!isCreate && !form.endzeit && <p className="mt-1 text-xs text-yellow-400">Laufend</p>}
            </div>
            <div className="min-w-0">
              <label className="label">Dauer</label>
              <div className="flex h-10 items-center text-sm text-dark-text" data-testid="flug-dauer">{formDauer}</div>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="min-w-0">
              <label className="label">Startort</label>
              <div className="flex items-center gap-2">
                <Input value={form.startort} onChange={e => setForm({...form, startort: e.target.value})} disabled={isDetail} placeholder="z.B. Hangar A, Koordinaten" className="w-full" />
                {!isDetail && <GpsButton onCoords={v => setForm({ ...form, startort: v })} />}
              </div>
              {ortHinweis(form.startort)}
            </div>
            <div className="min-w-0">
              <label className="label">Zielort</label>
              <div className="flex items-center gap-2">
                <Input value={form.zielort} onChange={e => setForm({...form, zielort: e.target.value})} disabled={isDetail} placeholder="z.B. Einsatzgebiet, Koordinaten" className="w-full" />
                {!isDetail && <GpsButton onCoords={v => setForm({ ...form, zielort: v })} title="Aktuelle GPS-Koordinaten als Landeort übernehmen" />}
              </div>
              {ortHinweis(form.zielort)}
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Flugart</label>
              <FlugartWahl value={form.flugart} onChange={v => setForm({...form, flugart: v})} disabled={isDetail} />
            </div>
            <div>
              <label className="label">Wetter</label>
              <Input value={form.wetter} onChange={e => setForm({...form, wetter: e.target.value})} disabled={isDetail} placeholder="z.B. klar, bewölkt, windig" />
            </div>
          </div>
        </div>
      </Card>
      )}

      {reiter === 'route' && !isCreate && currentFlug && (
        <Card>
          <FlugroutenKarte flug={currentFlug} />
        </Card>
      )}

      {reiter === 'aufnahmen' && !isCreate && currentFlug && (
        <Card>
          <FlugAufnahme flug={currentFlug} darfLoeschen />
        </Card>
      )}

      {reiter === 'vorkommnisse' && (
      <Card>
        <div className="space-y-4">
          <VorkommnisFelder wert={form.vorkommnis as VorkommnisWert} bemerkung={form.vorkommnis_bemerkung} disabled={isDetail} hms={flugHms}
            onChange={(vorkommnis, vorkommnis_bemerkung) => setForm({ ...form, vorkommnis, vorkommnis_bemerkung })} />
          <div>
            <label className="label">Bemerkungen</label>
            <Textarea value={form.bemerkungen} onChange={e => setForm({...form, bemerkungen: e.target.value})} disabled={isDetail} rows={3} />
          </div>
        </div>
      </Card>
      )}
      {!isDetail && <FormAktionsleiste geaendert={geaendert} speichert={speichert} speichernGesperrt={false} onSpeichern={speichern} onAbbrechen={zurueckZurListe} />}
    </div>
  )
}
